import { computed, shallowRef, type ComputedRef, type Ref } from "vue";

import type {
  EventId,
  FullNotice,
  Inbox,
  InboxElement,
  ReleasableRef,
} from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import {
  taskParentFilterKey,
  type TaskParentFilter,
} from "@/components/task/types";
import {
  bindAsync,
  releasableRef,
  releaseBestEffort,
  withRelease,
} from "@/ref";

export type InboxData = {
  // Raw inbox controller data
  items: Readonly<ReleasableRef<Inbox | null | undefined>>;

  // Items by category
  unplannedTasks: ComputedRef<InboxElement[]>;
  unarchivedNotices: Readonly<ReleasableRef<FullNotice[] | undefined>>;
  untitledPages: ComputedRef<InboxElement[]>;
  unansweredMeetings: ComputedRef<EventId[]>;

  // Counters by category
  unplannedTasksCount: ComputedRef<number>;
  unarchivedNoticesCount: ComputedRef<number>;
  untitledPagesCount: ComputedRef<number>;
  unansweredMeetingsCount: ComputedRef<number>;

  // Global counter
  counter: ComputedRef<number>;
};

export async function useInbox(
  inboxFilter: Ref<TaskParentFilter>
): Promise<Readonly<ReleasableRef<InboxData>>> {
  const controller = inject.controller();

  const inboxP = controller.inbox();
  // We want to show both viewed + unviewed notices in the inbox.
  // Archived notices should be removed from the inbox, so we exclude them here.
  const unarchivedNoticeIdsP = controller.notice.all({
    archived: "unarchived",
  });
  const unarchivedNoticesP = unarchivedNoticeIdsP.then((idsRef) =>
    bindAsync(idsRef, async (ids) => {
      const list = ids ?? [];
      if (list.length === 0) return releasableRef<FullNotice[]>([]);

      // Keep notice refs alive to be reactive.
      const noticeRefs = await Promise.all(
        list.map((id) => controller.notice.get(id))
      );

      const noticesRef = computed(() =>
        noticeRefs
          .map((r) => r.value)
          .filter(
            (n): n is FullNotice =>
              !!n &&
              // Extra safety: exclude archived notices even if server-side filtering changes.
              !n.archived
          )
      );

      return withRelease(noticesRef, () => {
        noticeRefs.forEach((r) => r.release());
      });
    })
  );

  const [inbox, unarchivedNoticeIds, unarchivedNotices] = await Promise.all([
    inboxP,
    unarchivedNoticeIdsP,
    unarchivedNoticesP,
  ]);

  const unansweredMeetings = computed<EventId[]>(
    () => inbox.value?.future ?? []
  );
  const unansweredMeetingsCount = computed(
    () => unansweredMeetings.value.length
  );

  const unplannedTasks = computed<InboxElement[]>(
    () =>
      inbox.value?.past.filter(
        (id: InboxElement) =>
          id.kind === "task" &&
          inboxFilter.value[taskParentFilterKey(id.parent?.kind)]
      ) || []
  );
  const unplannedTasksCount = computed(() => unplannedTasks.value.length);

  const untitledPages = computed<InboxElement[]>(
    () =>
      inbox.value?.past.filter((id: InboxElement) => id.kind === "page") || []
  );
  const untitledPagesCount = computed(() => untitledPages.value.length);

  const unarchivedNoticesCount = computed(
    () => unarchivedNotices.value?.length ?? 0
  );

  const counter = computed(
    () =>
      unarchivedNoticesCount.value +
      unansweredMeetingsCount.value +
      untitledPagesCount.value +
      unplannedTasksCount.value
  );

  const api: InboxData = {
    counter,
    items: inbox,
    unansweredMeetings,
    unansweredMeetingsCount,
    unplannedTasks,
    unplannedTasksCount,
    untitledPages,
    untitledPagesCount,
    unarchivedNotices,
    unarchivedNoticesCount,
  };

  let released = false;
  const release = () => {
    if (released) return;
    released = true;
    releaseBestEffort(inbox);
    releaseBestEffort(unarchivedNoticeIds);
    releaseBestEffort(unarchivedNotices);
  };

  // Important: use shallowRef so Vue doesn't unwrap nested refs/computed inside the API object.
  return withRelease(shallowRef(api), release);
}
